import React from 'react'
import { Typography, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
  ArticleComponent,
  ArticlesDisplayComponent as ArticleDisplayType,
} from '../../types/page'
import Link from '../Link'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { Article } from './Article'
import { Post } from '../../types/post'
import useLocation from 'react-use/lib/useLocation'

export interface ArticleDisplayProps {
  link: ArticleDisplayType
}

const ArticlesDisplay: React.FC<ArticleDisplayProps> = ({ link }) => {
  const { classes, cx } = useStyles()
  const { urlPathBuilder } = useUrlPathBuilder()
  const { pathname } = useLocation()

  let filteredArticles

  if (pathname?.includes('success-stories')) {
    // Get the name of the person from the URL
    // /new/success-stories/mike => mike
    const personSlug = pathname?.substring(pathname.lastIndexOf('/') + 1)

    // Programmatically filter out the self article from the list
    filteredArticles = link.articles.filter(
      (article) => article.slug !== personSlug
    )
  }

  const finalArticles = filteredArticles ?? link.articles

  const getLinkNode = (articleLink: ArticleDisplayType) =>
    articleLink.link.__typename === 'ContentfulInternalLink' ? (
      <Link to={urlPathBuilder([articleLink.link.slug ?? ''])}>
        {articleLink.link.text}
      </Link>
    ) : (
      articleLink.link && (
        <Link href={urlPathBuilder([articleLink.link.url])}>
          {articleLink.link.text}
        </Link>
      )
    )

  return (
    <Box>
      <Box className={classes.headerSection}>
        <Box>
          {link.title && (
            <Typography className={cx(classes.title)} variant="h2">
              {link.title}
            </Typography>
          )}
        </Box>
        <Box className={classes.linkLg}>{getLinkNode(link)}</Box>
      </Box>
      <Box className={classes.hr} />
      <Box className={cx(classes.container)}>
        {finalArticles.map((article) => (
          <ArticleDisplay article={article} key={article.contentful_id} />
        ))}
      </Box>
      <Box className={classes.linkSm}>{getLinkNode(link)}</Box>
    </Box>
  )
}

const ArticleDisplay: React.FC<{
  article: ArticleComponent | Post
}> = ({ article }) => {
  switch (article.__typename) {
    case 'ContentfulArticle':
      return (
        <Article
          slug={article.slug}
          title={article.title}
          excerpt={article.excerpt}
          featuredImage={article.featuredImage}
        />
      )
    case 'ContentfulPost':
      return (
        <Article
          slug={article.slug}
          title={article.title}
          excerpt={article.body}
          featuredImage={article.featuredImage}
        />
      )
    default:
      return null
  }
}

const useStyles = makeStyles()((theme) => ({
  container: {
    maxWidth: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    rowGap: '3rem',
    columnGap: '3rem',
    textAlign: 'left',
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      justifyContent: 'space-around',
    },
    alignItems: 'baseline',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      rowGap: '0',
    },
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },

  title: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },

  hr: {
    backgroundColor: theme.palette.divider,
    margin: '0.875rem 0 2rem',
    width: '100%',
    height: '1px',

    [theme.breakpoints.down('md')]: {
      margin: '0.375rem 0 2rem',
    },
  },
  linkLg: {
    fontWeight: 600,

    '@media (max-width: 375px)': {
      display: 'none',
    },
  },
  linkSm: {
    display: 'none',
    fontWeight: 600,

    '@media (max-width: 375px)': {
      display: 'flex',
      marginTop: '2.5rem',
    },
  },
}))

export default ArticlesDisplay
